import './index.css'
import PropTypes from 'prop-types'
import React, { memo, useMemo } from 'react'
import { connect } from 'react-redux'
import classNames from 'classnames'
import { VariableSizeList, areEqual } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import AutoSizer from 'react-virtualized-auto-sizer'

import Avatar from 'components/Utils/Avatar'
import { TooltipOnHover } from 'components/Utils/TooltipOnHover'
import * as Icons from 'components/Utils/Icons'
import { Text } from 'components/base'

import SelectRole from '../SelectRole'

import { Archive } from 'components/Users/Controls'
import { isArchived, isOwner } from 'data/users/record'
import { selectors as AuthSelectors } from 'data/auth'
import { sendReconciliationSSOEmail } from 'data/auth/actions'

const enhanceRow = connect(
  (state, { user }) => ({
    sendReconciliationSSOEmailStatus: AuthSelectors.getSendReconciliationSSOEmailStatus(
      state,
      user.id
    ),
  }),
  { sendReconciliationSSOEmail }
)

const User = enhanceRow(
  ({
    user,
    canManage,
    organization,
    sendReconciliationSSOEmail,
    sendReconciliationSSOEmailStatus,
    style,
  }) => {
    const shouldRenderSelector =
      canManage && !isArchived(user) && !isOwner(user)

    return (
      <div style={style}>
        <div
          style={{ width: 700 }}
          className={classNames(
            'organization-users-row padding-normal background-shade-100 background-shade-50-on-hover radius-large',
            'border-shade-45',
            'margin-left-auto',
            'margin-right-auto'
          )}
        >
          <div className="avatar flexbox flexbox-centered">
            <Avatar
              url={user.photoURL}
              name={user.get('displayName')}
              size="base"
            />
          </div>

          <Text.Name className="details-top">{user.displayName}</Text.Name>

          <Text.Detail className="details-bottom ellipsis">
            @{user.username}
            {!isArchived(user) && ` — ${user.email}`}
          </Text.Detail>

          {!isArchived(user) &&
            organization.get('authMethod') !== 'emailPassword' && (
              <div className="sso-status flexbox flexbox-align-center">
                <div className="flexbox flexbox-centered margin-right-normal">
                  <span className="inline-block margin-right-small shade-10">
                    SSO
                  </span>
                  {React.createElement(
                    user.ssoEnabledAt
                      ? Icons.CircleEmptyIcon
                      : Icons.TriangleEmptyIcon,
                    {
                      className: user.ssoEnabledAt ? 'green' : 'red',
                    }
                  )}
                </div>
                {!user.ssoEnabledAt && (
                  <>
                    {sendReconciliationSSOEmailStatus.ongoing && (
                      <Text.SmallDetail>Sending email...</Text.SmallDetail>
                    )}
                    {sendReconciliationSSOEmailStatus.success && (
                      <Text.SmallDetail>Binding email sent</Text.SmallDetail>
                    )}
                    {!sendReconciliationSSOEmailStatus.success &&
                      !sendReconciliationSSOEmailStatus.ongoing && (
                        <Text.Action
                          onClick={() =>
                            sendReconciliationSSOEmail({ userId: user.id })
                          }
                        >
                          Send binding email
                        </Text.Action>
                      )}
                  </>
                )}
              </div>
            )}

          <div className="role flexbox flexbox-centered">
            {shouldRenderSelector ? (
              <SelectRole user={user} popoverPlacement="left" />
            ) : (
              <Text.Detail className="capitalize">
                {isArchived(user) ? 'Deleted' : user.organizationRole}
              </Text.Detail>
            )}
          </div>

          <div className="archive flexbox flexbox-centered">
            {canManage && !isArchived(user) && !isOwner(user) ? (
              <Archive user={user}>
                <TooltipOnHover
                  id={`archive user ${user.id}`}
                  tooltipContent="Delete user"
                  placement="right"
                >
                  <div className="shade-30 red-on-hover pointer flexbox flexbox-centered">
                    <Icons.DeleteIcon size={14} />
                  </div>
                </TooltipOnHover>
              </Archive>
            ) : null}
          </div>
        </div>
      </div>
    )
  }
)

const UserRow = memo(({ index, style, data }) => {
  const { organization, canManage, users } = data
  const user = users[index]

  if (!user) {
    return null
  }

  return (
    <User
      style={style}
      key={user.id}
      user={user}
      canManage={canManage}
      organization={organization}
    />
  )
}, areEqual)

export default function UsersList({
  users,
  organization,
  canManage,
  listState,
  loadMore,
}) {
  const {
    loading,
    pageInfo: { hasNextPage },
  } = listState
  const isItemLoaded = index => !hasNextPage || index < users.length
  const itemCount = hasNextPage ? users.length + 1 : users.length
  const loadMoreItems = loading ? () => {} : loadMore

  const itemData = useMemo(
    () => {
      return { users, organization, canManage }
    },
    [users, organization, canManage]
  )

  return (
    <div
      className="margin-bottom-large"
      style={{
        height: `calc(100vh - 240px)`,
      }}
    >
      <InfiniteLoader
        isItemLoaded={isItemLoaded}
        itemCount={itemCount}
        loadMoreItems={loadMoreItems}
      >
        {({ onItemsRendered, ref }) => (
          <AutoSizer disableWidth>
            {({ height, width }) => (
              <VariableSizeList
                ref={ref}
                height={height}
                itemSize={index => (users[index] ? 64 : 0)}
                itemData={itemData}
                onItemsRendered={onItemsRendered}
                itemCount={listState.edges.length}
              >
                {UserRow}
              </VariableSizeList>
            )}
          </AutoSizer>
        )}
      </InfiniteLoader>
    </div>
  )
}

UsersList.propTypes = {
  users: PropTypes.array.isRequired,
  organization: PropTypes.object.isRequired,
  canManage: PropTypes.bool.isRequired,
}
